import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2, KeyRound, AlertCircle } from 'lucide-react';
import { formsApi } from '../../services/api';
import { FormSchema } from '../../types/form';
import PublicFormPage from './PublicFormPage';

const VerifySharedFormPage: React.FC = () => {
  const [shareKey, setShareKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verifiedForm, setVerifiedForm] = useState<FormSchema | null>(null);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!shareKey.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const response = await formsApi.verifyShareKey(shareKey.trim());
      if (response.success && response.form) {
        setVerifiedForm(response.form);
      } else {
        setError(response.message || 'Invalid key.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to verify key. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (verifiedForm) {
    return <PublicFormPage formSchemaProp={verifiedForm} handlerProp={verifiedForm.handler} />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md bg-white rounded-xl shadow-2xl p-8"
      >
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <KeyRound className="w-8 h-8 text-blue-600" />
        </div>
        
        <h2 className="text-2xl font-bold text-center text-gray-900 mb-2">Access Shared Form</h2>
        <p className="text-center text-gray-600 mb-8">
          Please enter the verify key provided to you to access this form.
        </p>

        <form onSubmit={handleVerify} className="space-y-6">
          <div>
            <label htmlFor="shareKey" className="block text-sm font-medium text-gray-700 mb-2">
              Verify Key
            </label>
            <input
              id="shareKey"
              type="text"
              value={shareKey}
              onChange={(e) => setShareKey(e.target.value)}
              placeholder="Enter your key..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
              required
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg">
              <AlertCircle className="w-4 h-4" />
              <p>{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !shareKey.trim()}
            className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-medium hover:shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Verifying...
              </>
            ) : (
              'Access Form'
            )}
          </button>
        </form>
      </motion.div>
    </div>
  );
};

export default VerifySharedFormPage;
